import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { QRCode } from 'react-qr-code';
import {
  Trash2,
  Copy,
  Check,
  Loader2,
  Info,
  MoreVertical,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useWorkspace } from '@/contexts/workspace-context';
import { workspaceUrl } from '@/lib/workspace-url';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useFetch } from '@/hooks/use-fetch';
import { useFacebookEnabled, useInstagramEnabled } from '@/hooks/use-app-config';
import {
  useSandboxSessionsStream,
  type SandboxSessionDTO,
} from '@/hooks/use-sandbox-sessions-stream';
import {
  useBindCode,
  useDeleteSandboxSession,
  useResetSessionWebhook,
} from '@/pages/sandbox/queries';
import { formatSandboxPhone } from '@/lib/format-sandbox-phone';
import { WebhookConfigDialog } from '@/components/webhook-config-dialog';
import { PageContainer } from '@/components/page-container';
import { docsUrl } from '@/lib/docs-url';
import { midEllipsisUrl } from '@/lib/mid-ellipsis';

/**
 * Phase 126: Single-surface sandbox onboarding.
 *
 * The old three-screen flow (button → phone-input dialog → activation-code
 * dialog) is replaced by one landing page:
 *   1. Helper text telling the dev to send a message from their phone
 *   2. "Add Testing Session" primary button that opens wa.me with the user's
 *      bind code pre-filled in the message body
 *   3. Adjacent 128x128 QR code encoding the same wa.me URL (single source of
 *      truth — button and QR always resolve to the same deep link)
 *   4. Sessions table below — active only (backend filters out any other
 *      status per Plan 03; this page defensively filters too)
 *
 * PostHog session-creation funnel event is NOT captured from the dashboard —
 * Plan 04 moves that capture into the forwarder's bind-match branch where the
 * bind actually resolves to a session row. Keeping the event emission out of
 * the browser avoids the double-emission pitfall (grep 126-RESEARCH §Pitfall 1).
 */

export interface SandboxSession {
  id: string;
  workspaceId: string;
  createdByUserId: string;
  phone: string | null;
  accessToken: string;
  status: string;
  webhookUrl: string | null;
  cloudflareTunnelId: string | null;
  hostname: string | null;
  hmacSecret: string;
  /** GET-handshake echo value probed by webhook verify. Distinct from
   *  hmacSecret (Verify Token ≠ HMAC). */
  verifyToken: string;
  activatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** STI discriminator. Optional for backward compat with legacy rows
   *  rendered before the IG rollout — readers default to 'whatsapp'. */
  type?: 'whatsapp' | 'instagram' | 'facebook';
  /** facebook-only (AIT-621) — PSID and resolved display name of the Messenger sender. */
  facebookSenderId?: string | null;
  facebookSenderName?: string | null;
  /** IG-only — opaque IGSID of the customer that bound this session.
   *  Renamed from `instagramSenderId` so the field name self-documents
   *  the IG actor (sender = the end user). */
  senderInstagramId?: string | null;
  /** IG-only — sandbox IG Business account this session is anchored on.
   *  Renamed from `instagramAccountId` so the field name self-documents
   *  the IG actor (account = the shared sandbox account). */
  accountInstagramId?: string | null;
  /** IG-only — customer handle resolved via Meta Profile API at bind
   *  time. NULL while the async resolver hasn't completed yet (the
   *  BOUND payload races the fire-and-forget Meta lookup) or when
   *  the resolve failed. Display layer prepends `@` and falls back to
   *  `senderInstagramId` (raw IGSID) when null. Renamed from
   *  `instagramSenderUsername` to match the senderInstagram* pattern. */
  senderInstagramUsername?: string | null;
}

const SANDBOX_NUMBER = import.meta.env.VITE_SANDBOX_PHONE_NUMBER ?? '';
const SANDBOX_IG_USERNAME =
  import.meta.env.VITE_SANDBOX_IG_USERNAME ?? 'hookmyappsandboxstaging';
// AIT-621: the sandbox Page's m.me handle (or numeric id). Empty = not configured.
const SANDBOX_FB_PAGE_USERNAME = import.meta.env.VITE_SANDBOX_FB_PAGE_USERNAME ?? '';

type SandboxChannel = 'whatsapp' | 'instagram' | 'facebook';

// ---------------------------------------------------------------------------
// CopyButton
// ---------------------------------------------------------------------------

export function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    await navigator.clipboard.writeText(text);
    toast.success('Copied!');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Button variant="ghost" size="icon-xs" onClick={handleCopy} aria-label="Copy">
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// SandboxHeader — page title + description
// ---------------------------------------------------------------------------

function SandboxHeader({ channels }: { channels: string[] }) {
  const list = channels.length > 1 ? `${channels.slice(0, -1).join(', ')} or ${channels[channels.length - 1]}` : channels[0];
  return (
    <div className="shrink-0">
      <h1 className="text-2xl font-semibold tracking-tight">Build and test your app via {list}</h1>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BindActionSurface — hero bind-code card with destination, code, QR, and CTA
// ---------------------------------------------------------------------------

function BindActionSurface({
  bindCode,
  bindCodeError,
  conflictError,
}: {
  bindCode: string | null;
  bindCodeError: boolean;
  conflictError: string | null;
}) {
  const instagramEnabled = useInstagramEnabled();
  // Like Quick Start: the tab needs a configured sandbox Page, not just the flag.
  const facebookEnabled = useFacebookEnabled() && SANDBOX_FB_PAGE_USERNAME !== '';
  const [channel, setChannel] = useState<SandboxChannel>('whatsapp');
  const [codeCopied, setCodeCopied] = useState(false);
  const formattedPhone = formatSandboxPhone(SANDBOX_NUMBER);
  const code = bindCode ? encodeURIComponent(bindCode) : '';
  // Per-channel copy and links. ig.me cannot prefill text; wa.me and m.me can.
  const surface = {
    whatsapp: {
      title: 'HookMyApp Sandbox test number',
      destination: formattedPhone,
      instruction: "Send the code from your phone and we'll connect it so you can start sending and receiving test messages.",
      url: bindCode ? `https://wa.me/${SANDBOX_NUMBER}?text=${code}` : '',
      qrLabel: 'QR code linking to WhatsApp with your bind code pre-filled',
      cta: 'Send In WhatsApp',
    },
    instagram: {
      title: 'HookMyApp Sandbox Instagram account',
      destination: `@${SANDBOX_IG_USERNAME}`,
      instruction: "DM the code from your Instagram account and we'll connect it so you can start sending and receiving test messages.",
      url: bindCode ? `https://ig.me/m/${SANDBOX_IG_USERNAME}` : '',
      qrLabel: 'QR code linking to Instagram DM with the sandbox account',
      cta: 'Send In Instagram',
    },
    facebook: {
      title: 'HookMyApp Sandbox Facebook Page',
      destination: SANDBOX_FB_PAGE_USERNAME ? `m.me/${SANDBOX_FB_PAGE_USERNAME}` : 'Not configured',
      instruction: "Message the code to the Page from your Facebook account and we'll connect it so you can start sending and receiving test messages.",
      url: bindCode && SANDBOX_FB_PAGE_USERNAME ? `https://m.me/${SANDBOX_FB_PAGE_USERNAME}` : '',
      qrLabel: 'QR code linking to Messenger with the sandbox Page',
      cta: 'Send In Messenger',
    },
  }[channel];

  function handleClick() {
    if (!surface.url) return;
    window.open(surface.url, '_blank');
  }

  async function handleCopyCode() {
    if (!bindCode) return;
    await navigator.clipboard.writeText(bindCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 1800);
  }

  const disabled = !bindCode || bindCodeError || !surface.url;
  const isWhatsapp = channel === 'whatsapp';
  const { title, destination, instruction, qrLabel, url: qrUrl, cta: ctaLabel } = surface;
  const showTabs = instagramEnabled || facebookEnabled;

  return (
    <div className="shrink-0 space-y-3">
      {showTabs && (
        <Tabs value={channel} onValueChange={(v) => setChannel(v as SandboxChannel)}>
          <TabsList>
            <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
            {instagramEnabled && <TabsTrigger value="instagram">Instagram</TabsTrigger>}
            {facebookEnabled && <TabsTrigger value="facebook">Facebook</TabsTrigger>}
          </TabsList>
        </Tabs>
      )}

      <section className="rounded-lg border bg-card">
        <div className="grid gap-6 p-6 md:grid-cols-2 md:items-center md:gap-8">
          {/* Left — intro + code + instructions */}
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold tracking-tight">{title}</h3>
              <h4
                className={`mt-1 text-lg font-medium${
                  isWhatsapp ? ' tabular-nums' : ''
                }`}
              >
                {destination}
              </h4>
            </div>

            <p className="max-w-xs text-sm text-muted-foreground">
              {instruction}
            </p>

            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Your Sandbox session code
              </p>
              <div className="mt-1 flex items-center gap-2">
                <h4 className="font-mono text-lg font-semibold tracking-[0.08em] text-foreground">
                  {bindCode ?? '…'}
                </h4>
                {bindCode && (
                  <button
                    type="button"
                    onClick={handleCopyCode}
                    className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={codeCopied ? 'Code copied' : 'Copy code'}
                    title={codeCopied ? 'Copied' : 'Copy to clipboard'}
                  >
                    {codeCopied ? (
                      <Check className="size-3.5" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {conflictError && (
              <p className="text-sm text-destructive" role="alert">
                {conflictError}
              </p>
            )}
            {bindCodeError && !conflictError && (
              <p className="text-sm text-destructive" role="alert">
                Couldn&apos;t load your code. Try again in a moment.
              </p>
            )}
          </div>

          {/* Right — QR + CTA, stacked (QR on top, button under) */}
          <div className="flex flex-col items-center gap-2">
            {isWhatsapp && (
              <p className="text-center text-xs text-muted-foreground">
                Scan with your phone&apos;s camera, not WhatsApp.
              </p>
            )}
            <div className="rounded-md border bg-background p-2">
              {bindCode ? (
                <>
                  <QRCode value={qrUrl} size={140} aria-label={qrLabel} />
                  <span className="sr-only">Link: {qrUrl}</span>
                </>
              ) : (
                <div className="flex size-[180px] items-center justify-center">
                  <Loader2 className="size-8 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
            <Button
              size="default"
              onClick={handleClick}
              disabled={disabled}
            >
              {ctaLabel}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WebhookEditDialog — verify-modal wrapper for sandbox sessions.
// Uses WebhookConfigDialog (same flow as channels) so the PATCH includes
// the server-side verify probe before persisting.
// ---------------------------------------------------------------------------

export function WebhookEditDialog({
  open,
  onOpenChange,
  session,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: SandboxSession | null;
  onSaved: (updated: SandboxSession) => void;
}) {
  if (!session) return null;

  const tunnelUrl = session.hostname ? `https://${session.hostname}/webhook` : null;
  const hasCustomUrl = session.webhookUrl !== null && session.webhookUrl !== tunnelUrl;

  function handleConfigured(webhookUrl: string, verifyToken: string) {
    // Optimistically reflect the saved URL + verify token. SSE stream will
    // overwrite with the server's authoritative patch when it arrives.
    onSaved({ ...session!, webhookUrl, verifyToken });
    onOpenChange(false);
  }

  return (
    <WebhookConfigDialog
      open={open}
      onOpenChange={onOpenChange}
      onConfigured={handleConfigured}
      mode="edit"
      currentWebhookUrl={hasCustomUrl ? (session.webhookUrl ?? '') : ''}
      currentVerifyToken={session.verifyToken}
      // Empty fallback on purpose: never prefill another credential (the bind
      // code) as the verify token. Blank submits are treated by the backend as
      // "keep the stored token".
      fallbackVerifyToken=""
      titleOverride="Update Sandbox Webhook"
      descriptionOverride="Set where sandbox webhooks should be delivered. Your server must accept POST requests with a 2xx status, and return this Verify Token in the body of a GET request so we can verify the URL."
      verifyTokenHelp="A handshake value — your server echoes it back on a GET so we know we're hitting the right URL. It is not the webhook signing secret. Keep the pre-filled value or replace it with your own."
      submitConfig={{
        endpoint: `/sandbox/sessions/${session.id}/webhook-url`,
        method: 'PATCH',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// ResetToCliDialog — separate confirm dialog for switching back to HookMyApp CLI.
// ---------------------------------------------------------------------------

export function ResetToCliDialog({
  open,
  onOpenChange,
  session,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: SandboxSession | null;
  onSaved: (updated: SandboxSession) => void;
}) {
  const resetWebhook = useResetSessionWebhook();
  const resetting = resetWebhook.isPending;

  if (!session) return null;

  function handleReset() {
    resetWebhook.mutate(
      { sessionId: session!.id },
      {
        onSuccess: (updated) => {
          toast.success('Switched back to HookMyApp CLI');
          onSaved(updated);
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.userMessage ?? 'Failed to reset webhook URL');
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Switch to HookMyApp CLI?</DialogTitle>
          <DialogDescription>
            Inbound webhooks will stop being delivered to your current URL and
            will instead be forwarded through the HookMyApp CLI. You can change
            the destination back at any time.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={resetting}>
            Cancel
          </Button>
          <Button onClick={handleReset} disabled={resetting}>
            {resetting && <Loader2 className="size-3.5 animate-spin" />}
            Switch to CLI
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// DeleteConfirmDialog
// ---------------------------------------------------------------------------

function DeleteConfirmDialog({
  open,
  onOpenChange,
  session,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: SandboxSession | null;
  onDeleted: (id: string) => void;
}) {
  const { activeWorkspace } = useWorkspace();
  const deleteSession = useDeleteSandboxSession(activeWorkspace?.id);
  const deleting = deleteSession.isPending;

  if (!session) return null;

  function handleDelete() {
    deleteSession.mutate(
      { sessionId: session!.id },
      {
        onSuccess: () => {
          toast.success('Session deleted');
          onDeleted(session!.id);
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err.userMessage ?? 'Failed to delete session');
        },
      },
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Session</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure? This will permanently remove this sandbox session and
            release the Cloudflare tunnel.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting && <Loader2 className="size-3.5 animate-spin" />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// DestinationCell — explains where test messages go in user-facing terms
// ---------------------------------------------------------------------------
//
// Two user-facing states (the product model is NOT the backend null state):
//   1. Custom URL stored (webhookUrl set AND not the internal tunnel URL)
//      → "Your URL" + the URL + copy button
//   2. Otherwise (no custom URL, OR webhookUrl is the internal tunnel URL)
//      → "HookMyApp CLI" + info icon tooltip
//
// Absence of a custom URL is NOT shown as empty / missing. The default path
// is the CLI flow; users on that path do not need to see the internal
// webhook URL.
function DestinationCell({ session }: { session: SandboxSession }) {
  const tunnelUrl = session.hostname
    ? `https://${session.hostname}/webhook`
    : null;
  const hasCustomUrl =
    session.webhookUrl !== null && session.webhookUrl !== tunnelUrl;

  if (hasCustomUrl && session.webhookUrl) {
    return (
      <div className="flex items-center gap-1">
        <span
          className="truncate max-w-[220px] text-sm"
          title={session.webhookUrl}
        >
          {midEllipsisUrl(session.webhookUrl)}
        </span>
        <CopyButton text={session.webhookUrl} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-sm font-medium">HookMyApp CLI</span>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label="About the HookMyApp CLI flow"
              className="inline-flex text-muted-foreground hover:text-foreground"
            />
          }
        >
          <Info className="size-3.5" />
        </TooltipTrigger>
        <TooltipContent>
          Messages are delivered to your app by the HookMyApp CLI.
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChannelBadge — channel-discriminated tag for the Sender row
// ---------------------------------------------------------------------------

function ChannelBadge({ type }: { type: SandboxChannel }) {
  return <span>{{ whatsapp: 'WhatsApp', instagram: 'Instagram', facebook: 'Facebook' }[type]}</span>;
}

// ---------------------------------------------------------------------------
// SenderCell — provider-aware Sender column body. WA shows phone, IG shows
// resolved `@username` (with raw IGSID as fallback when the async username
// resolve hasn't populated `senderInstagramUsername` yet or hiccupped).
// ---------------------------------------------------------------------------

function SenderCell({ session }: { session: SandboxSession }) {
  if (session.type === 'facebook') {
    // Display name once resolved, PSID until then (same fallback rule as IG).
    const label = session.facebookSenderName ?? session.facebookSenderId ?? null;
    if (label) {
      return (
        <div className="flex items-center gap-1">
          <span>{label}</span>
          <CopyButton text={label} />
        </div>
      );
    }
    return <span className="text-muted-foreground">Resolving…</span>;
  }
  if (session.type === 'instagram') {
    const username = session.senderInstagramUsername ?? null;
    if (username) {
      return (
        <div className="flex items-center gap-1">
          <span>@{username}</span>
          <CopyButton text={username} />
        </div>
      );
    }
    const igsid = session.senderInstagramId ?? null;
    if (igsid) {
      return (
        <div className="flex items-center gap-1">
          <span>{igsid}</span>
          <CopyButton text={igsid} />
        </div>
      );
    }
    return <span className="text-muted-foreground">Resolving…</span>;
  }
  if (session.phone) {
    return (
      <div className="flex items-center gap-1">
        <span>{session.phone}</span>
        <CopyButton text={session.phone} />
      </div>
    );
  }
  return <span>—</span>;
}

// ---------------------------------------------------------------------------
// SessionsTable — active-only, no Status or Code columns
// ---------------------------------------------------------------------------

function SessionsTable({
  sessions,
  onSessionRemoved,
}: {
  sessions: SandboxSession[];
  onSessionRemoved: (id: string) => void;
}) {
  const [deleteSession, setDeleteSession] = useState<SandboxSession | null>(null);
  const { activeWorkspace } = useWorkspace();

  const activeSessions = sessions.filter((s) => s.status === 'active');

  if (activeSessions.length === 0) {
    return null;
  }

  return (
    <>
      <Card className="!overflow-visible md:min-h-0 md:flex-1">
        <CardHeader className="shrink-0">
          <CardTitle>Connected testing sessions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 md:min-h-0 md:flex-1">
          <div className="scrollable-table md:min-h-0 md:flex-1 md:[&_[data-slot=table-container]]:h-full">
        <Table className="table-fixed max-md:min-w-[44rem]">
          <colgroup>
            <col style={{ width: '11%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '32%' }} />
            <col style={{ width: '15%' }} />
          </colgroup>
          <TableHeader className="sticky top-0 z-10 bg-card pointer-events-none">
            <TableRow>
              <TableHead className="px-3">Channel</TableHead>
              <TableHead className="px-3">Sender</TableHead>
              <TableHead className="px-3">Sandbox session code</TableHead>
              <TableHead className="px-3">
                <span className="inline-flex items-center gap-1">
                  Inbound Destination
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <button
                          type="button"
                          aria-label="About inbound destination"
                          className="pointer-events-auto inline-flex items-center justify-center leading-none p-0 text-muted-foreground/60 hover:text-muted-foreground"
                        />
                      }
                    >
                      <Info className="size-3.5" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Where each sandbox session's inbound events are
                      delivered. Either a webhook URL you configured for the
                      session, or the HookMyApp CLI.
                    </TooltipContent>
                  </Tooltip>
                </span>
              </TableHead>
              <TableHead className="px-3 text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeSessions.map((session) => (
                <TableRow key={session.id}>
                  <TableCell className="px-3">
                    <ChannelBadge type={session.type ?? 'whatsapp'} />
                  </TableCell>
                  <TableCell className="px-3">
                    <SenderCell session={session} />
                  </TableCell>
                  <TableCell className="px-3">
                    <div className="flex items-center gap-1">
                      <span className="truncate font-mono text-sm" title={session.accessToken}>
                        {session.accessToken}
                      </span>
                      <CopyButton text={session.accessToken} />
                    </div>
                  </TableCell>
                  <TableCell className="px-3">
                    <DestinationCell session={session} />
                  </TableCell>
                  <TableCell className="px-3 text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            aria-label="Session actions"
                            title="Session actions"
                          >
                            <MoreVertical className="size-3.5" />
                          </Button>
                        }
                      />
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          render={
                            <Link
                              to={
                                activeWorkspace?.id
                                  ? workspaceUrl(
                                      activeWorkspace.id,
                                      `sandbox/sessions/${session.id}`,
                                    )
                                  : '#'
                              }
                            >
                              <Info className="size-3.5" />
                              Details
                            </Link>
                          }
                        />
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteSession(session)}
                          variant="destructive"
                        >
                          <Trash2 className="size-3.5" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={deleteSession !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteSession(null);
        }}
        session={deleteSession}
        onDeleted={(id) => {
          onSessionRemoved(id);
          setDeleteSession(null);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// SandboxPage (exported)
// ---------------------------------------------------------------------------

// Persist the "How to use the sandbox" help banner dismissal so it stays gone
// across reloads once the user closes it.
const SANDBOX_HELP_DISMISSED_KEY = 'sandbox_help_alert_dismissed';

// Map the all-optional wire shape (SandboxSessionDTO) to the all-required app
// shape (SandboxSession) by reading each field explicitly. The DTO is NOT
// structurally assignable to SandboxSession — required app fields
// (workspaceId, createdByUserId, accessToken, hmacSecret, createdAt,
// updatedAt) are optional on the wire — so a blanket `as` would hide a
// genuinely missing field. The backend always populates these for active
// sessions; the fallbacks only satisfy the type system for edge payloads.
function toSandboxSession(dto: SandboxSessionDTO): SandboxSession {
  return {
    id: dto.id,
    workspaceId: dto.workspaceId ?? '',
    createdByUserId: dto.createdByUserId ?? '',
    phone: dto.phone,
    accessToken: dto.accessToken ?? '',
    status: dto.status,
    webhookUrl: dto.webhookUrl ?? null,
    cloudflareTunnelId: dto.cloudflareTunnelId ?? null,
    hostname: dto.hostname ?? null,
    hmacSecret: dto.hmacSecret ?? '',
    verifyToken: dto.verifyToken ?? '',
    activatedAt: dto.activatedAt ?? null,
    createdAt: dto.createdAt ?? '',
    updatedAt: dto.updatedAt ?? '',
    type: dto.type,
    senderInstagramId: dto.senderInstagramId,
    accountInstagramId: dto.accountInstagramId,
    senderInstagramUsername: dto.senderInstagramUsername,
    // Stream BOUND events carry the PSID only as the generic senderId.
    facebookSenderId: dto.facebookSenderId ?? (dto.type === 'facebook' ? dto.senderId : null) ?? null,
    facebookSenderName: dto.facebookSenderName ?? null,
  };
}

const PHONE_ALREADY_BOUND_MESSAGE =
  'This number is already bound to another workspace. Remove the existing binding first.';

export function SandboxPage() {
  const { sessions: streamSessions, loading, removeSession, applySnapshot } = useSandboxSessionsStream();
  const sessions = streamSessions.map(toSandboxSession);
  const { activeWorkspace, sessionScoped } = useWorkspace();
  const instagramEnabled = useInstagramEnabled();
  const facebookEnabled = useFacebookEnabled() && SANDBOX_FB_PAGE_USERNAME !== '';
  // Bind code via TanStack Query. Any non-2xx flips the query to its error
  // state so the inline retry copy renders beneath the button; the 409
  // PHONE_ALREADY_BOUND conflict is derived from the thrown AppError's code.
  const bindCodeQuery = useBindCode(activeWorkspace?.id, sessionScoped);
  const bindCode = bindCodeQuery.data?.code ?? null;
  const bindCodeError = bindCodeQuery.isError;
  const bindError = bindCodeQuery.error;
  const conflictError =
    bindError?.code === 'PHONE_ALREADY_BOUND' ? PHONE_ALREADY_BOUND_MESSAGE : null;
  const [helpDismissed, setHelpDismissed] = useState(
    () => localStorage.getItem(SANDBOX_HELP_DISMISSED_KEY) === 'true',
  );

  // Toast once per conflict error instance — matches the pre-Query behavior
  // where every failed bind-code fetch with 409 re-toasted.
  useEffect(() => {
    if (bindError?.code === 'PHONE_ALREADY_BOUND') {
      toast.error(PHONE_ALREADY_BOUND_MESSAGE);
    }
  }, [bindError]);

  function dismissHelp() {
    setHelpDismissed(true);
    localStorage.setItem(SANDBOX_HELP_DISMISSED_KEY, 'true');
  }
  const fetchWithAuth = useFetch();

  // Tracks session IDs seen since mount — null means first snapshot hasn't
  // arrived yet; we skip the toast on initial load so existing sessions don't
  // fire as "new".
  const knownIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    // Wait for the first SSE snapshot before initializing the seen-ids set.
    // Without this gate, the initial render with sessions=[] (hook default)
    // sets knownIdsRef=Set{} BEFORE any data arrives — then every session
    // in the first real snapshot looks "new" and fires a spurious toast.
    if (loading) return;
    const prev = knownIdsRef.current;
    if (prev === null) {
      knownIdsRef.current = new Set(sessions.map((s) => s.id));
      return;
    }
    for (const s of sessions) {
      if (!prev.has(s.id) && s.status === 'active') {
        toast.success(
          s.phone ? `${s.phone} connected to sandbox` : 'Phone connected to sandbox',
        );
      }
    }
    knownIdsRef.current = new Set(sessions.map((s) => s.id));
  }, [sessions, loading]);

  // Phase 130 §4b/§4c — handoff JWT capture + POST /sandbox/claim-handoff
  // are owned by useDemoHandoffClaim mounted in WorkspaceProvider. Sessions
  // now arrive via the SSE stream; bind-code still uses a separate endpoint.

  // Refetch bind-code on tab return — user came back from WhatsApp; bind may
  // have consumed. Listen on both `visibilitychange` and `focus` because older
  // mobile Safari sometimes fires only one of the two (TanStack's focusManager
  // only watches visibilitychange, so keep the manual wiring). Sessions are
  // delivered via the persistent SSE stream and need no manual refetch.
  const refetchBindCode = bindCodeQuery.refetch;
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === 'visible') void refetchBindCode();
    }
    function handleFocus() {
      void refetchBindCode();
    }
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refetchBindCode]);

  // Focus/visibility safety net catches dropped SSE events. Mirrors quick-start.tsx —
  // one-shot per tab return, not a poll.
  useEffect(() => {
    const safetyRefetch = async () => {
      try {
        const res = await fetchWithAuth('/sandbox/sessions');
        if (!res.ok) return;
        const data = (await res.json()) as SandboxSessionDTO[];
        applySnapshot(data);
      } catch {
        // best-effort; SSE auto-reconnect or full reload covers worst case
      }
    };
    const focusHandler = () => { void safetyRefetch(); };
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') void safetyRefetch();
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    window.addEventListener('focus', focusHandler);
    return () => {
      document.removeEventListener('visibilitychange', visibilityHandler);
      window.removeEventListener('focus', focusHandler);
    };
  }, [fetchWithAuth, applySnapshot]);

  const activeSessions = sessions.filter((s) => s.status === 'active');

  if (loading) {
    return (
      <div className="mx-auto flex h-full max-w-4xl flex-col items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <PageContainer variant="fill" className="gap-6 pb-6 max-md:h-auto">
      <SandboxHeader
        channels={['WhatsApp', ...(instagramEnabled ? ['Instagram'] : []), ...(facebookEnabled ? ['Facebook'] : [])]}
      />

      <BindActionSurface
        bindCode={bindCode}
        bindCodeError={bindCodeError}
        conflictError={conflictError}
      />

      {activeSessions.length > 0 && !helpDismissed && (
        <Alert className="relative shrink-0 border-blue-500/30 bg-blue-500/5">
          <Info className="text-blue-500" />
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={dismissHelp}
            aria-label="Dismiss"
            title="Dismiss"
            className="absolute right-2 top-2 text-muted-foreground"
          >
            <X className="size-3.5" />
          </Button>
          <AlertTitle>How to use the sandbox</AlertTitle>
          <AlertDescription>
            <p>
              We recommend using HookMyApp CLI. If you&apos;re just getting
              started, the easiest option is our CLI with the starter kit.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  window.open(
                    docsUrl('build-with-ai/cli'),
                    '_blank',
                  )
                }
              >
                CLI Only
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  window.open(
                    docsUrl('build-with-ai/starter-kit'),
                    '_blank',
                  )
                }
              >
                CLI + Starter Kit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  window.open(
                    docsUrl('platform/diy'),
                    '_blank',
                  )
                }
              >
                DIY
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <SessionsTable
        sessions={sessions}
        onSessionRemoved={removeSession}
      />
    </PageContainer>
  );
}
